import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { matchMeetingSignal } from '@confluence/experiment-meeting-signal-core/entry-points/matchMeetingSignal.ts';
import { matchesMeetingTemplate } from '@confluence/experiment-meeting-signal-core/entry-points/matchesMeetingTemplate.ts';
import type { MeetingSignalInput } from '@confluence/experiment-meeting-signal-core/entry-points/meetingSignalTypes.ts';

import type { FireOnceGuard } from './createFireOnceGuard.ts';

export type EvaluateMeetingSignalParams = MeetingSignalInput & {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	/** In-memory fire-once guard for this surface's page load. */
	guard?: FireOnceGuard;
	titleKeywords?: readonly string[];
	contentKeywords?: readonly string[];
};

/**
 * The shared detection pipeline, applied at every surface:
 *
 * 1. Fire-once — bail if this surface already fired for this page load.
 * 2. Match — title/content lists (whole-word) and/or the meeting-template set.
 * 3. Check — whether any signal matched now.
 * 4. Fire — only on a positive match, then mark the guard. The single GAS v3
 *    `meetingSignal detected` track event carries only `pageType` + `trigger`.
 *    No keyword text/count, template id, locale, page/space id, or page content
 *    is ever sent. `pageType` + `trigger` is the full analytical payload; the
 *    matched signals are checked only to decide whether to fire.
 *
 * Returns `true` if it fired the event, `false` otherwise (no match or already
 * fired). Callers can pass whichever of title/content/template
 * inputs are knowable at their trigger.
 */
export const evaluateAndMaybeFireMeetingSignal = (params: EvaluateMeetingSignalParams): boolean => {
	const {
		createAnalyticsEvent,
		pageType,
		trigger,
		guard,
		titleText,
		titleKeywords,
		contentText,
		contentKeywords,
		template,
	} = params;

	if (guard?.hasFired()) {
		return false;
	}

	const titleMatched =
		!!titleText && !!titleKeywords && matchMeetingSignal(titleText, titleKeywords);
	const contentMatched =
		!!contentText && !!contentKeywords && matchMeetingSignal(contentText, contentKeywords);
	const templateMatched = !!template && matchesMeetingTemplate(template);

	if (!titleMatched && !contentMatched && !templateMatched) {
		return false;
	}

	guard?.markFired();
	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'detected',
			actionSubject: 'meetingSignal',
			attributes: {
				pageType,
				trigger,
			},
		},
	}).fire();

	return true;
};
